import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Video, Lightbulb, Lock, Quote, FileText, Mic } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { getTheme, themeStyle, eventTitle } from "@/lib/eventTheme";
import { Logo } from "@/components/Common";

function useCountdown(deadline) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!deadline) return null;
  const diff = new Date(deadline) - now;
  if (diff <= 0) return { passed: true };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { passed: false, d, h, m, s };
}

const TIPS = [
  "Find a quiet, well-lit spot",
  "Hold your phone horizontally",
  "Look into the camera and speak from the heart",
  "Keep it short — a minute is plenty",
];

export default function TributePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | pin | ok | notfound
  const [pin, setPin] = useState("");
  const cd = useCountdown(ev?.settings?.deadline);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/tribute/${slug}`);
        setEv(res.data);
        // Invitation tracking: capture ?i=<token>, remember it, and mark 'opened'.
        const token = new URLSearchParams(window.location.search).get("i");
        if (token) {
          try { sessionStorage.setItem(`gathervoices_invite_${slug}`, token); } catch {}
          api.post(`/tribute/${slug}/track-open`, { invite_token: token }).catch(() => {});
        }
        // Lightweight analytics beacon (once per session per tribute)
        try {
          const key = `gathervoices_seen_${slug}`;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            api.post("/track", { name: "link_open", slug, meta: { has_invite: !!token } }).catch(() => {});
          }
        } catch {}
        if (res.data.has_pin) setStatus("pin"); else { setStatus("ok"); loadGallery(); }
      } catch { setStatus("notfound"); }
    })();
  }, [slug]);

  const loadGallery = async () => {
    try { const g = await api.get(`/tribute/${slug}/gallery`); setGallery(g.data); } catch {}
  };

  const verifyPin = async (e) => {
    e.preventDefault();
    try { await api.post(`/tribute/${slug}/verify-pin`, { pin }); setStatus("ok"); loadGallery(); }
    catch { const t = await import("sonner"); t.toast.error("Incorrect PIN"); }
  };

  if (status === "loading") return <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--accent))] border-t-transparent animate-spin" /></div>;
  if (status === "notfound") return (
    <div className="min-h-screen grid place-items-center px-4 text-center">
      <div><h1 className="font-display text-3xl font-semibold">Tribute not found</h1><p className="mt-2 text-[hsl(var(--muted-foreground))]">This link may have expired or is incorrect.</p><Link to="/" className="mt-4 inline-block underline">Go home</Link></div>
    </div>
  );

  const theme = getTheme(ev.type);
  const full = ev.capacity?.full;
  const closed = ev.status !== "collecting" || cd?.passed || full;

  if (status === "pin") return (
    <div className="min-h-screen grid place-items-center px-4" style={themeStyle(ev.type)}>
      <form onSubmit={verifyPin} className="w-full max-w-sm rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 text-center">
        <div className="h-12 w-12 mx-auto grid place-items-center rounded-full" style={{ background: `hsl(${theme.accent})` }}><Lock className="text-white" size={20} /></div>
        <h1 className="mt-4 font-display text-2xl font-semibold">This tribute is protected</h1>
        <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">Enter the PIN shared by the organizer.</p>
        <input data-testid="pin-entry-input" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" className="mt-5 w-full h-12 text-center rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
        <button data-testid="pin-submit-button" className="mt-4 w-full h-12 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold">Continue</button>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen" style={themeStyle(ev.type)}>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 -z-10" style={{ backgroundImage: theme.heroWash }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-4 flex flex-col items-center gap-2">
          <Logo />
          {ev.branding?.name && (
            <div data-testid="tribute-branding" className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
              {ev.branding.logo_file_id && <img src={fileUrl(ev.branding.logo_file_id)} alt="" className="h-6 w-6 rounded object-cover" />}
              Presented with care by <strong className="text-[hsl(var(--foreground))]">{ev.branding.name}</strong>
            </div>
          )}
        </div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mx-auto px-4 sm:px-6 pb-10 text-center">
          {ev.photos?.[0] && (
            <div className="mx-auto w-40 sm:w-52 aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_16px_40px_rgba(20,16,12,0.18)] border-4 border-white">
              <img src={fileUrl(ev.photos[0])} alt={ev.honoree_name} className="h-full w-full object-cover" />
            </div>
          )}
          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: `hsl(${theme.accent})` }}>{theme.framing}</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold tracking-[-0.02em]">{ev.honoree_name}</h1>
          {ev.type === "memorial" && (ev.dates?.start || ev.dates?.end) && (
            <p className="mt-2 text-[hsl(var(--muted-foreground))]">{fmtYear(ev.dates?.start)} — {fmtYear(ev.dates?.end)}</p>
          )}
          {ev.relationship && <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{ev.relationship}</p>}
        </motion.div>
      </section>

      {/* Story + CTA */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 pb-8">
        {ev.story && (
          <div className="rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 sm:p-8">
            <Quote size={22} style={{ color: `hsl(${theme.accent})` }} />
            <p className="mt-3 leading-relaxed whitespace-pre-wrap text-[hsl(var(--foreground))]">{ev.story}</p>
          </div>
        )}

        {/* Countdown + CTA */}
        <div className="mt-8 text-center">
          {closed ? (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6" data-testid="tribute-closed-card">
              {full ? (
                <>
                  <p className="font-semibold">This collection is full</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">This tribute has reached the free plan's limit of {ev.capacity?.max} messages. Please reach out to the organizer — they can upgrade to welcome more voices.</p>
                </>
              ) : (
                <>
                  <p className="font-semibold">Submissions are now closed</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Thank you to everyone who shared. The family is putting the tribute together.</p>
                </>
              )}
            </div>
          ) : (
            <>
              {cd && (
                <div data-testid="countdown-pill" className="inline-flex items-center gap-3 rounded-full px-5 py-2 text-sm font-semibold mb-5" style={{ background: `hsl(${theme.chipBg})`, color: `hsl(${theme.accentInk})` }}>
                  {cd.d > 0 && <span>{cd.d}d</span>}<span>{pad(cd.h)}h</span><span>{pad(cd.m)}m</span><span className="tabular-nums">{pad(cd.s)}s</span><span className="opacity-70">left to share</span>
                </div>
              )}
              <div>
                <button data-testid="public-tribute-record-button" onClick={() => navigate(`/t/${slug}/record`)}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl text-lg font-semibold text-white shadow-[0_14px_34px_rgba(20,16,12,0.20)] hover:opacity-95 transition-opacity active:translate-y-[1px]"
                  style={{ background: `hsl(${theme.accent})` }}>
                  <Video size={22} /> Record your tribute
                </button>
              </div>
              <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">{theme.invite.replace("{name}", ev.honoree_name)}</p>
            </>
          )}
        </div>

        {/* Tips + prompts */}
        {!closed && (
          <div className="mt-10 grid gap-5">
            <div className="rounded-2xl p-5 border-l-4" style={{ background: `hsl(${theme.accentSoft})`, borderColor: `hsl(${theme.accent})` }}>
              <div className="flex items-center gap-2 font-semibold" style={{ color: `hsl(${theme.accentInk})` }}><Lightbulb size={18} /> A few gentle tips</div>
              <ul className="mt-3 space-y-1.5 text-sm" style={{ color: `hsl(${theme.accentInk})` }}>
                {TIPS.map((t, i) => <li key={i} className="flex items-start gap-2"><span className="mt-2 h-1 w-1 rounded-full" style={{ background: `hsl(${theme.accent})` }} /> {t}</li>)}
                <li className="flex items-start gap-2"><span className="mt-2 h-1 w-1 rounded-full" style={{ background: `hsl(${theme.accent})` }} /> Keep it under {ev.max_clip_seconds || ev.settings?.max_clip_length || 60} seconds</li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-[hsl(var(--muted-foreground))]">Not sure what to say? Try one of these</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(ev.prompts || []).map((p, i) => (
                  <span key={i} data-testid={`prompt-chip-${i}`} className="rounded-full px-4 py-2 text-sm font-medium" style={{ background: `hsl(${theme.chipBg})`, color: `hsl(${theme.accentInk})` }}>{p}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Wall of memories (approved) */}
        {gallery.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display text-2xl font-semibold text-center">A wall of memories</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {gallery.map((s) => (
                <div key={s.submission_id} className="rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] overflow-hidden">
                  {s.kind === "video" && <video src={fileUrl(s.file_id)} controls playsInline className="w-full bg-black" />}
                  {s.kind === "audio" && <div className="p-4 flex items-center gap-3"><div className="h-10 w-10 grid place-items-center rounded-full shrink-0" style={{ background: `hsl(${theme.accent})`, color: "#fff" }}><Mic size={18} /></div><audio src={fileUrl(s.file_id)} controls className="w-full" /></div>}
                  {s.kind === "text" && <div className="p-5"><FileText size={16} style={{ color: `hsl(${theme.accent})` }} /><p className="mt-2 leading-relaxed">{s.message}</p></div>}
                  <div className="px-4 py-3 border-t border-[hsl(var(--border))]"><span className="font-semibold text-sm">{s.contributor_name}</span>{s.relationship && <span className="text-sm text-[hsl(var(--muted-foreground))]"> · {s.relationship}</span>}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
      <div className="h-10" />
    </div>
  );
}

const pad = (n) => String(n).padStart(2, "0");
const fmtYear = (d) => (d ? new Date(d).getFullYear() : "");
